import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { PanelSkeleton, Skeleton } from '@/components/ui/skeleton';
import { RoleForm } from '../components/RoleForm';
import { PermissionsPanel } from '../components/PermissionsPanel';
import { PermissionHistoryModal } from '../components/PermissionHistoryModal';
import { ApiErrorSummary } from '@/components/ApiErrorSummary';
import { getApiErrorMessage, getApiErrorDetails, type ApiErrorDetails } from '@/lib/getApiErrorMessage';
import { roleService } from '../services';
import { useAuthorizationStore } from '../store';
import type { RoleDto } from '../types';
import type { CreateRoleFormData } from '../schemas';

export function EditRolePage() {
  usePageTitle('Edit Role');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [role, setRole] = useState<RoleDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<ApiErrorDetails | null>(null);

  const {
    permissionsByModule,
    isLoadingPermissions,
    fetchPermissions,
    selectRoleForAuth,
    selectedRoleForAuth,
    openPermissionHistoryModal,
  } = useAuthorizationStore();

  // Sync local role state when the store's selectedRoleForAuth changes (e.g., after permission save)
  useEffect(() => {
    if (selectedRoleForAuth) {
      setRole(selectedRoleForAuth);
    }
  }, [selectedRoleForAuth]);

  useEffect(() => {
    if (!id) return;

    const fetchRole = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const roleData = await roleService.getRoleById(Number(id));
        setRole(roleData);
        selectRoleForAuth(roleData);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Failed to fetch role'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchRole();
    fetchPermissions();
  }, [id, fetchPermissions, selectRoleForAuth]);

  const handleBack = () => {
    navigate('/administration/authorization');
  };

  const handleEditSubmit = async (data: CreateRoleFormData) => {
    if (!role) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const updatedRole = await roleService.updateRole(role.id, {
        name: data.name,
        description: data.description || undefined,
        isActive: data.isActive,
      });
      setRole(updatedRole);
      selectRoleForAuth(updatedRole);
      toast.success('Role details updated successfully.');
    } catch (err) {
      const errorDetails = getApiErrorDetails(err, 'Failed to update role');
      setSubmitError(errorDetails);
      toast.error(errorDetails.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <PanelSkeleton rows={4} />
      </div>
    );
  }

  if (error || !role) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Authorization
        </Button>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <p className="text-destructive">{error || 'Role not found'}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleBack}
        className="gap-1"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Authorization
      </Button>

      <div className="flex items-center gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">{role.name}</h1>
            <Badge variant={role.isActive ? 'success' : 'secondary'}>
              {role.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            <code>{role.roleCode}</code>
          </p>
        </div>
      </div>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Role Details</TabsTrigger>
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
        </TabsList>
        <TabsContent value="details" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Role Details</CardTitle>
              <CardDescription>
                Update the role's information.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ApiErrorSummary error={submitError} />
              <RoleForm
                role={role}
                onSubmit={handleEditSubmit}
                onCancel={handleBack}
                isEditMode
                isSubmitting={isSubmitting}
              />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="permissions" className="mt-4">
          <Card className="overflow-hidden">
            {isLoadingPermissions ? (
              <PanelSkeleton rows={8} className="m-4 border-0 p-0" />
            ) : (
              <PermissionsPanel
                role={role}
                permissionsByModule={permissionsByModule}
                onOpenHistory={openPermissionHistoryModal}
                readOnly={role.isSystemRole}
              />
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <PermissionHistoryModal />
    </div>
  );
}
